import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

/**
 * Closing "Redefining Modern Living" section for the Better-Built Living
 * page. Full-bleed background image with the final brand statement and a
 * text link to the manufacturing page.
 *
 * Tokens live under `betterbuiltcta.*` so content stays page-exclusive.
 */

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  // Pull from a later gallery index than the hero / featured image so the
  // closing CTA shows a different photo than the page banner above.
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[7].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[7].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 7,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A warm, aspirational "home" image for this specific property that lands the Better-Built Living message — a polished building exterior, bright staged interior, or signature architectural detail that reads premium and lasting. Explicitly pick something DIFFERENT from the property featured image / hero shot so the closing section does not repeat what the page banner already showed.',
    context:
      'Full-bleed background for the closing "Redefining Modern Living" section on the Better-Built Living page. Must visually differ from the page hero banner.',
  },
};

const linkDef: SlotGroupDefinition = {
  label: 'Manufacturing Page Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/manufacturing' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Our Manufacturing Page' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function BetterBuiltCTA() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  const bg = useSlotGroup('betterbuiltcta.bg_image', bgImageDef);
  const link = useSlotGroup('betterbuiltcta.link', linkDef);

  return (
    <section className="relative w-full overflow-hidden">
      <EditableSlotGroup groupPath="betterbuiltcta.bg_image" definition={bgImageDef}>
        <div className="absolute inset-0">
          {bg.image ? (
            <VibesitesImage
              url={bg.image}
              alt={bg.alt || ''}
              sizes="100vw"
              className="w-full h-full object-cover object-center"
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-primary)]" />
          )}
        </div>
      </EditableSlotGroup>

      <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/45" />

      <div
        ref={sectionRef}
        className="reveal-fade-up relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 text-center text-white"
      >
        <RichTextContent
          path="betterbuiltcta.title"
          label="Title"
          defaultValue="<p>Redefining Modern Living</p>"
          as="h2"
          className="font-heading text-3xl sm:text-4xl md:text-5xl font-normal leading-tight tracking-tight max-w-3xl mx-auto drop-shadow"
        />
        <RichTextContent
          path="betterbuiltcta.body"
          label="Body"
          defaultValue='<p>At <span data-type="field-token" data-field="property_name"></span>, we&rsquo;re combining innovation, sustainability, and thoughtful design to transform the way you live. More than just a home, it&rsquo;s a space crafted to enhance your lifestyle. That&rsquo;s what we call &ldquo;Better-Built Living&rdquo;.</p>'
          as="div"
          className="mt-6 font-body text-base sm:text-lg leading-relaxed text-white/90 max-w-2xl mx-auto [&_p]:mb-0"
        />

        <div className="mt-10 flex items-center justify-center">
          <EditableSlotGroup groupPath="betterbuiltcta.link" definition={linkDef}>
            <a
              href={link.url || '/manufacturing'}
              target={link._target || '_self'}
              className="inline-flex items-center gap-2 font-body text-sm font-semibold uppercase tracking-[0.2em] text-white border-b border-white/60 hover:border-white hover:gap-3 transition-all duration-200 pb-1"
            >
              <span>{link.label || 'Visit Our Manufacturing Page'}</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </a>
          </EditableSlotGroup>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'BetterBuiltCTA',
  displayName: 'Better-Built CTA',
  tokens: {
    'betterbuiltcta.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Redefining Modern Living</p>',
    },
    'betterbuiltcta.body': {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>At <span data-type="field-token" data-field="property_name"></span>, we&rsquo;re combining innovation, sustainability, and thoughtful design to transform the way you live. More than just a home, it&rsquo;s a space crafted to enhance your lifestyle. That&rsquo;s what we call &ldquo;Better-Built Living&rdquo;.</p>',
    },
  },
  slotGroups: {
    'betterbuiltcta.bg_image': bgImageDef,
    'betterbuiltcta.link': linkDef,
  },
};

(BetterBuiltCTA as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default BetterBuiltCTA;
